import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment-timezone";
import { toast } from "sonner";
import Dialog from "../../../../../../components/shared/Dialog";
import Button from "../../../../../../components/shared/Button";
import BaseForm from "../../../../../../components/shared/Form";
import Table from "./Table";
import Schedule from "./Schedule";
import Template from "./Template";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import { useCopyMutations } from "../../../api/useCopyMutations";
import { useUser } from "../../../../../../utils/hooks/user";
import { getOrderedWeekdays } from "../../../helpers/getOrderedWeekdays";
import { formatTimeSheetParams } from "../../../../../TimeSheet/api/helpers";
import { enumerateDaysBetweenDates } from "../../../../../TimeSheet/ManagerView/Table/useColumns";
import { cn } from "../../../../../../lib/utils";
import { createCopySchema } from "./zodValidation";
import type { CopyFormValues, CopyValues } from "./types";
import "./style.scss";

interface CopyProps {
    visible: boolean;
    close: () => void;
    values: CopyValues | null;
}

function Copy({ visible, close, values }: CopyProps) {
    const { t } = useTranslation();
    const user = useUser();
    const [data, setData] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [dates, setDates] = useState<{ from: string | undefined, to: string | undefined }>({ from: undefined, to: undefined });
    const { type, dispatch, resetCount, clearSelectedEvents, refetchEventStatistics } = useScheduleContext();
    const isTeamScheduler = type === "team";
    const [filters] = useFilters();
    const orderedWeekdays = getOrderedWeekdays();

    const schema = useMemo(() => createCopySchema({ t, type, values }), [t, type, values]);

    const form = useForm<CopyFormValues>({
        defaultValues: { users: [], day: undefined },
        resolver: zodResolver(schema)
    });

    const onSuccess = useCallback((response: any) => {
        setIsSubmitted(true);
        if (!!response?.runOnBackground) {
            toast.info(t("bulk-copy-success", { employees: response?.totalUsers, events: response?.totalEvents }));
        } else {
            setData(response);
        }
        clearSelectedEvents();
        resetCount?.();
        refetchEventStatistics();
    }, [resetCount, refetchEventStatistics, clearSelectedEvents, t]);

    const { cellMutation, userMutation, bulkMutation, isLoading } = useCopyMutations({ values, onSuccess });

    const onCopy = useCallback((value: CopyFormValues) => {
        let from = filters?.from;
        let to = filters?.to;
        if (filters?.mode === "month") {
            from = moment(filters?.from).startOf("week").format("YYYY-MM-DD");
            to = moment(filters?.to).endOf("week").format("YYYY-MM-DD");
        }

        if (values?.type === "bulk-day") {
            from = moment(values?.date).format("YYYY-MM-DD");
            to = moment(values?.date).format("YYYY-MM-DD")
        }

        const params = {
            from,
            to,
            filters: {
                pageIndex: Number(filters?.page) - 1,
                pageSize: filters?.pageSize,
                ...formatTimeSheetParams({
                    filters: filters?.filters,
                    from,
                    to,
                }),
                sort: filters?.sort
            }
        }
        if (values?.type === "bulk-day" || (values?.type === "bulk" && filters?.mode === "month")) {
            delete params.filters.pageIndex;
            delete params.filters.pageSize;
        }
        if (!dates.from && !dates.to && ["team", "home"].includes(type)) {
            setError(t("required-date"));
        } else {
            if (values?.type === "cell") {
                const selectedDates = enumerateDaysBetweenDates(
                    moment(dates?.from),
                    moment(dates?.to || dates?.from)
                )?.map((date: any) => moment(date).format("YYYY-MM-DD"));
                let data: any = {
                    userIds: isTeamScheduler
                        ? value?.users?.map((user: any) => user?.id)
                        : [user?.id],
                    dates: selectedDates
                };
                if (values?.isTemplate) {
                    data = {
                        targetDay: value.day,
                        targetUserIds: value.users.map((user: any) => user.id)
                    }
                }
                cellMutation.mutate(data);
            } else if (values?.type === "row") {
                let data: any = {
                    userIds: isTeamScheduler
                        ? value?.users?.map((user: any) => user?.id)
                        : [user?.id],
                    filters: params,
                    from: params?.from,
                    to: params?.to,
                    newDate: moment(dates?.from).format("YYYY-MM-DD")
                };
                if (values?.isTemplate) {
                    data = {
                        targetUserIds: value?.users?.map((user: any) => user?.id),
                        filters: params,
                        days: value.day,
                    }
                }
                userMutation.mutate(data);
            } else if (values?.type === "bulk-day" || values?.type === "day") {
                let data: any = {
                    newDate: moment(dates.from).format("YYYY-MM-DD"),
                    ids: values?.ids,
                    from: values?.date,
                    to: values?.date,
                    filters: {
                        pageIndex: Number(filters?.page) - 1,
                        pageSize: filters?.pageSize,
                        ...formatTimeSheetParams({
                            filters: filters?.filters,
                            from: values?.date,
                            to: values?.date
                        }),
                        sort: filters?.sort
                    }
                };
                if (values?.isTemplate) {
                    data = {
                        filters: {
                            pageIndex: Number(filters?.page) - 1,
                            pageSize: filters?.pageSize,
                            ...formatTimeSheetParams({
                                filters: filters?.filters,
                            } as any),
                            sort: filters?.sort
                        },
                        sourceDay: orderedWeekdays[moment(values?.date).weekday()],
                        targetDay: value.day,
                        // ids: values?.ids,
                    }
                }
                bulkMutation.mutate(data);
            } else {
                const data = {
                    newDate: moment(dates.from).format("YYYY-MM-DD"),
                    ...params
                };
                bulkMutation.mutate(data);
            }
        }
    }, [filters, values, dates, type, t, isTeamScheduler, user?.id, cellMutation, userMutation, bulkMutation, orderedWeekdays]);

    const content = useMemo(() => {
        if (["team", "home"].includes(type)) {
            return (
                <Schedule
                    values={values}
                    dates={dates}
                    setDates={setDates}
                    error={error}
                    setError={setError}
                />
            );
        } else {
            return <Template values={values} />;
        }
    }, [type, values, error, dates]);

    const onClose = useCallback(() => {
        setIsSubmitted(false);
        if (isSubmitted) {
            const success = data?.find(({ success }) => success);
            if (success) {
                dispatch({ type: "refetch-events" });
            }
        }
        close();
        setDates({ from: undefined, to: undefined });
        setData([]);
        setError(null);
        form.reset();
    }, [isSubmitted, close, data, dispatch, form]);

    return (
        <Dialog
            open={visible}
            close={onClose}
            contentClassName="tw-min-w-2xl tw-max-w-3xl tw-max-h-[85vh] tw-flex tw-flex-col"
            bodyClassName="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col tw-overflow-hidden"
            title={
                !values?.type || values?.type === "row"
                    ? t("copy-shifts")
                    : t("copy-one-shift")
            }
            footer={(
                <div className="tw-flex tw-justify-end tw-gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        loading={isLoading}
                        disabled={isLoading}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        className={cn(isSubmitted && "tw-hidden")}
                        type="submit"
                        form="copy-form"
                        loading={isLoading}
                        disabled={isLoading}
                    >
                        {t("copy")}
                    </Button>
                </div>
            )}
        >
            <BaseForm
                id="copy-form"
                form={form}
                onFinish={onCopy}
                className="tw-flex-1 tw-min-h-0 tw-text-center"
            >
                {isSubmitted ? (
                    <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col tw-text-start">
                        <Table data={data} />
                    </div>
                ) : content}
            </BaseForm>
        </Dialog>
    )
}

export default Copy;
